// src/components/ledger/AllAccounts.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, query, orderBy, doc, updateDoc, getDoc, 
  Timestamp, addDoc 
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper: format number with thousand separators (UGX)
const formatMoney = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US');
};

export const AllAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Add Account modal states
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Transaction modal states
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionAccount, setTransactionAccount] = useState(null);
  const [transactionAmountRaw, setTransactionAmountRaw] = useState('');   // numeric value
  const [transactionAmountDisplay, setTransactionAmountDisplay] = useState(''); // formatted display
  const [transactionType, setTransactionType] = useState('salary');
  const [transactionDescription, setTransactionDescription] = useState('');
  const [transactionMonth, setTransactionMonth] = useState('');
  const [transactionYear, setTransactionYear] = useState(new Date().getFullYear().toString());
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Pagination for modals
  const [salaryPage, setSalaryPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const [itemsPerModalPage] = useState(5);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const accountsRef = collection(db, 'accountsPayable');
      const snapshot = await getDocs(query(accountsRef, orderBy('createdAt', 'desc')));
      const accountsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // ----- Add Account Handler -----
  const handleAddAccount = async (formData) => {
    try {
      const payableData = {
        type: formData.type,
        name: formData.name,
        phone: formData.phone || '',
        email: formData.email || '',
        description: formData.description || '',
        amount: 0,
        currentBalance: 0,
        paidAmount: 0,
        status: 'pending',
        paymentType: formData.paymentType || (formData.type === 'employee' ? 'monthly' : 'one_time'),
        monthlySalary: formData.type === 'employee' ? 0 : null,
        salaryHistory: [],
        transactionHistory: [],
        paymentHistory: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      await addDoc(collection(db, 'accountsPayable'), payableData);
      await fetchAccounts();
      setShowAddModal(false);
      alert('Account added successfully! You can now add salary/bonus/penalty.');
    } catch (error) {
      console.error('Error adding account:', error);
      alert('Error adding account: ' + error.message);
    }
  };

  const getPeriodKey = (month, year) => {
    const monthNumber = new Date(Date.parse(month + " 1, " + year)).getMonth() + 1;
    return `${year}-${String(monthNumber).padStart(2, '0')}`;
  };

  const handleAddTransaction = async () => {
    const amount = parseFloat(transactionAmountRaw);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
  
    if (!transactionDescription.trim()) {
      alert('Please enter a description');
      return;
    }
  
    if (transactionType === 'salary' && !transactionMonth) {
      alert('Please select a month for salary accrual');
      return;
    }
  
    setTransactionLoading(true);
    try {
      const payableRef = doc(db, 'accountsPayable', transactionAccount.id);
      const currentDoc = await getDoc(payableRef);
      const current = currentDoc.data();
      
      let newBalance = current.currentBalance || 0;
      let newTotalAmount = current.amount || 0;
      
      if (transactionType === 'penalty') {
        newBalance = newBalance - amount;
        newTotalAmount = newTotalAmount - amount;
      } else {
        newBalance = newBalance + amount;
        newTotalAmount = newTotalAmount + amount;
      }
      
      const transactionRecord = {
        id: Date.now().toString(),
        type: transactionType,
        amount: amount,
        date: Timestamp.now(),
        description: transactionDescription,
        month: transactionType === 'salary' ? transactionMonth : null,
        year: transactionType === 'salary' ? transactionYear : null,
        period: transactionType === 'salary' ? getPeriodKey(transactionMonth, transactionYear) : null,
        previousBalance: current.currentBalance,
        newBalance: newBalance,
        createdBy: 'accountant'
      };
      
      let updatedSalaryHistory = [...(current.salaryHistory || [])];
      if (transactionType === 'salary') {
        const periodKey = getPeriodKey(transactionMonth, transactionYear);
        const existingEntryIndex = updatedSalaryHistory.findIndex(h => h.period === periodKey);
        
        if (existingEntryIndex !== -1) {
          updatedSalaryHistory[existingEntryIndex] = {
            ...updatedSalaryHistory[existingEntryIndex],
            amount: (updatedSalaryHistory[existingEntryIndex].amount || 0) + amount,
            status: 'pending',
            updatedAt: Timestamp.now(),
            description: transactionDescription
          };
        } else {
          updatedSalaryHistory.push({
            period: periodKey,
            month: transactionMonth,
            year: transactionYear,
            amount: amount,
            status: 'pending',
            description: transactionDescription,
            dueDate: Timestamp.fromDate(new Date(parseInt(transactionYear), 
              new Date(Date.parse(transactionMonth + " 1, " + transactionYear)).getMonth(), 28)),
            createdAt: Timestamp.now()
          });
        }
      }
      
      const updatedTransactionHistory = [...(current.transactionHistory || []), transactionRecord];
      const newStatus = newBalance === 0 ? 'paid' : (newBalance < newTotalAmount ? 'partial' : 'pending');
      
      await updateDoc(payableRef, {
        currentBalance: newBalance,
        amount: newTotalAmount,
        salaryHistory: updatedSalaryHistory,
        transactionHistory: updatedTransactionHistory,
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      
      // Journal entry
      const journalEntries = transactionType === 'salary' ? [
        { account: 'salaries_expense', accountName: 'Salaries Expense', debit: amount, credit: 0, description: transactionDescription },
        { account: 'salaries_payable', accountName: 'Salaries Payable', debit: 0, credit: amount, description: `Salary accrual for ${transactionAccount.name} - ${transactionMonth} ${transactionYear}` }
      ] : transactionType === 'bonus' ? [
        { account: 'bonus_expense', accountName: 'Bonus Expense', debit: amount, credit: 0, description: transactionDescription },
        { account: 'bonus_payable', accountName: 'Bonus Payable', debit: 0, credit: amount, description: `Bonus for ${transactionAccount.name}` }
      ] : transactionType === 'penalty' ? [
        { account: 'penalty_receivable', accountName: 'Penalty Receivable', debit: amount, credit: 0, description: `Penalty deduction: ${transactionDescription}` },
        { account: 'penalty_income', accountName: 'Penalty Income', debit: 0, credit: amount, description: `Penalty applied to ${transactionAccount.name}` }
      ] : [
        { account: 'adjustment_account', accountName: 'Adjustment Account', debit: amount, credit: 0, description: transactionDescription },
        { account: 'accounts_payable', accountName: 'Accounts Payable', debit: 0, credit: amount, description: `Adjustment for ${transactionAccount.name}` }
      ];
      
      await addDoc(collection(db, 'journalEntries'), {
        journalId: `JOURNAL-${Date.now()}`,
        transactionId: payableRef.id,
        transactionType: `payable_${transactionType}`,
        date: Timestamp.now(),
        entries: journalEntries,
        createdAt: Timestamp.now(),
        createdBy: 'accountant'
      });
      
      await fetchAccounts();
      setShowTransactionModal(false);
      setTransactionAccount(null);
      setTransactionAmountRaw('');
      setTransactionAmountDisplay('');
      setTransactionDescription('');
      setTransactionMonth('');
      alert(`${transactionType === 'salary' ? 'Salary' : transactionType === 'bonus' ? 'Bonus' : transactionType=='adjustment'?'Adjustment' : 'Penalty'} recorded successfully!`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    } finally {
      setTransactionLoading(false);
    }
  };

  const generateStatement = (account) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 50, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('ACCOUNT STATEMENT', pageWidth / 2, 30, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 65);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Account Details:', 20, 85);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${account.name}`, 20, 100);
    doc.text(`Type: ${account.type === 'employee' ? 'Employee' : account.type === 'subcontractor' ? 'Subcontractor' : 'Supplier'}`, 20, 110);
    doc.text(`Phone: ${account.phone || 'N/A'}`, 20, 120);
    doc.text(`Email: ${account.email || 'N/A'}`, 20, 130);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Summary:', 20, 150);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Total Amount Payable: ${(account.amount || 0).toLocaleString()} UGX`, 20, 165);
    doc.text(`Total Amount Paid: ${(account.paidAmount || 0).toLocaleString()} UGX`, 20, 175);
    doc.text(`Current Balance Due: ${(account.currentBalance || 0).toLocaleString()} UGX`, 20, 185);
    
    let yPos = 205;
    if (account.transactionHistory && account.transactionHistory.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Transaction History:', 20, yPos);
      yPos += 10;
      
      const transactionRows = [...(account.transactionHistory || [])].reverse().map(t => [
        t.date?.toDate ? new Date(t.date.toDate()).toLocaleDateString() : 'N/A',
        t.type === 'salary' ? 'Salary' : t.type === 'bonus' ? 'Bonus' : t.type === 'penalty' ? 'Penalty' : 'Adjustment',
        `${t.type === 'penalty' ? '-' : '+'}${t.amount?.toLocaleString()} UGX`,
        t.description,
        t.period || '-',
        `${t.newBalance?.toLocaleString()} UGX`
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Type', 'Amount', 'Description', 'Period', 'Balance']],
        body: transactionRows,
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    if (account.paymentHistory && account.paymentHistory.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment History:', 20, yPos);
      yPos += 10;
      
      const paymentRows = account.paymentHistory.map(p => [
        p.date?.toDate ? new Date(p.date.toDate()).toLocaleDateString() : 'N/A',
        p.amount?.toLocaleString() || '0',
        p.method || '-',
        p.reference || '-',
        `${p.newBalance?.toLocaleString()} UGX`
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Amount', 'Method', 'Reference', 'Balance After']],
        body: paymentRows,
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        styles: { fontSize: 8 },
        margin: { left: 20, right: 20 }
      });
    }
    
    doc.save(`${account.name}_Statement_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getFilteredAccounts = () => {
    let filtered = [...accounts];
    if (filterType !== 'all') filtered = filtered.filter(a => a.type === filterType);
    if (filterName) filtered = filtered.filter(a => a.name?.toLowerCase().includes(filterName.toLowerCase()));
    if (filterStatus !== 'all') filtered = filtered.filter(a => a.status === filterStatus);
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredAccounts();
    return filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  };

  const getTotalPages = () => Math.ceil(getFilteredAccounts().length / itemsPerPage);

  const getTypeIcon = (type) => {
    switch(type) {
      case 'employee': return '👨‍💼';
      case 'subcontractor': return '👷';
      case 'supplier': return '🏭';
      default: return '📋';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'paid': return { background: '#d1fae5', color: '#059669', text: '✓ Paid' };
      case 'partial': return { background: '#fef3c7', color: '#d97706', text: '⏳ Partial' };
      default: return { background: '#fee2e2', color: '#dc2626', text: '⚠️ Pending' };
    }
  };

  const accountTotals = () => {
    const totalAmount = accounts.reduce((sum, a) => sum + (a.amount || 0), 0);
    const totalPaid = accounts.reduce((sum, a) => sum + (a.paidAmount || 0), 0);
    const totalBalance = accounts.reduce((sum, a) => sum + (a.currentBalance || 0), 0);
    return { totalAmount, totalPaid, totalBalance };
  };

  const totals = accountTotals();
  const paginatedData = getPaginatedData();
  const totalPages = getTotalPages();

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = [2024, 2025, 2026, 2027];

  const paginatedSalaryHistory = selectedAccount?.salaryHistory?.slice((salaryPage - 1) * itemsPerModalPage, salaryPage * itemsPerModalPage) || [];
  const paginatedPaymentHistory = selectedAccount?.paymentHistory?.slice((paymentPage - 1) * itemsPerModalPage, paymentPage * itemsPerModalPage) || [];
  const paginatedTransactionHistory = [...(selectedAccount?.transactionHistory || [])].reverse().slice((transactionPage - 1) * itemsPerModalPage, transactionPage * itemsPerModalPage);

  const salaryTotalPages = Math.ceil((selectedAccount?.salaryHistory?.length || 0) / itemsPerModalPage);
  const paymentTotalPages = Math.ceil((selectedAccount?.paymentHistory?.length || 0) / itemsPerModalPage);
  const transactionTotalPages = Math.ceil((selectedAccount?.transactionHistory?.length || 0) / itemsPerModalPage);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading accounts...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>All Accounts</h1>
          <p style={styles.subtitle}>Manage employees, subcontractors, and suppliers</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.addButton} onClick={() => setShowAddModal(true)}>
            + Add Account
          </button>
        </div>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💰</div>
          <div>
            <div style={styles.summaryLabel}>Total Payables</div>
            <div style={styles.summaryValue}>{totals.totalAmount.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💳</div>
          <div>
            <div style={styles.summaryLabel}>Total Paid</div>
            <div style={styles.summaryValue}>{totals.totalPaid.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={{...styles.summaryCard, ...styles.warningCard}}>
          <div style={styles.summaryIcon}>⚖️</div>
          <div>
            <div style={styles.summaryLabel}>Balance Due</div>
            <div style={styles.summaryValue}>{totals.totalBalance.toLocaleString()} UGX</div>
          </div>
        </div>
      </div>

      <div style={styles.infoNote}>
        <span>ℹ️</span>
        <span>Click "Update Account" to record salary accruals, bonuses, or penalties.</span>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Account Type</label>
          <select style={styles.filterSelect} value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Types</option>
            <option value="employee">Employees</option>
            <option value="carpenter">Carpenter</option>
            <option value="subcontractor">Subcontractors</option>
            <option value="supplier">Suppliers</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search Name</label>
          <input type="text" style={styles.filterInput} placeholder="Search by name..." value={filterName} onChange={(e) => { setFilterName(e.target.value); setCurrentPage(1); }} />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Payment Status</label>
          <select style={styles.filterSelect} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Type</th>
              <th style={styles.tableHeader}>Name</th>
              <th style={styles.tableHeader}>Contact</th>
              <th style={styles.tableHeader}>Description</th>
              <th style={styles.tableHeader}>Amount Paid</th>
              <th style={styles.tableHeader}>Balance Due</th>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr><td colSpan="8" style={styles.emptyState}>No accounts found</td></tr>
            ) : (
              paginatedData.map(account => {
                const statusBadge = getStatusBadge(account.status);
                return (
                  <tr key={account.id} style={styles.tableRow}>
                    <td style={styles.tableCell}><span style={{ fontSize: '20px' }}>{getTypeIcon(account.type)}</span></td>
                    <td style={{...styles.tableCell, fontWeight: '600'}}>{account.name}</td>
                    <td style={styles.tableCell}>
                      {account.phone && <div>📞 {account.phone}</div>}
                      {account.email && <div>✉️ {account.email}</div>}
                    </td>
                    <td style={styles.tableCell}>{account.description || '-'}</td>
                    <td style={styles.tableCell}>{(account.paidAmount || 0).toLocaleString()} UGX</td>
                    <td style={{...styles.tableCell, ...((account.currentBalance || 0) > 0 ? styles.balanceDue : styles.balanceZero)}}>
                      {(account.currentBalance || 0).toLocaleString()} UGX
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{...styles.statusBadge, background: statusBadge.background, color: statusBadge.color}}>
                        {statusBadge.text}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button style={styles.transactionButton} onClick={() => { setTransactionAccount(account); setShowTransactionModal(true); }}>
                          📝 Update Account
                        </button>
                        <button style={styles.viewButton} onClick={() => { setSelectedAccount(account); setShowModal(true); }}>
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button style={{...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
          <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
          <button style={{...styles.pageButton, ...(currentPage === totalPages ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddAccount}
        />
      )}

      {/* Transaction Modal */}
      {showTransactionModal && transactionAccount && (
        <div style={modalStyles.overlay} onClick={() => setShowTransactionModal(false)}>
          <div style={{...modalStyles.container, maxWidth: '550px'}} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>Update This Account</h2>
              <button style={modalStyles.closeButton} onClick={() => setShowTransactionModal(false)}>×</button>
            </div>
            <div style={modalStyles.content}>
              <div style={styles.accountInfoBox}>
                <div><span>Account:</span><strong>{transactionAccount.name}</strong></div>
                <div><span>Current Balance:</span><strong>{(transactionAccount.currentBalance || 0).toLocaleString()} UGX</strong></div>
              </div>
              
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Transaction Type *</label>
                <select style={modalStyles.select} value={transactionType} onChange={(e) => setTransactionType(e.target.value)}>
                  <option value="salary">💰 Salary Accrual (Adds to balance)</option>
                  <option value="bonus">🎁 Bonus (Adds to balance)</option>
                  <option value="penalty">⚠️ Penalty (Subtracts from balance)</option>
                  <option value="adjustment">📝 Adjustment (Special Transactions)</option>
                </select>
              </div>
              
              {transactionType === 'salary' && (
                <div style={modalStyles.formRow}>
                  <div style={{ flex: 1 }}>
                    <label style={modalStyles.label}>Month *</label>
                    <select style={modalStyles.select} value={transactionMonth} onChange={(e) => setTransactionMonth(e.target.value)}>
                      <option value="">Select Month</option>
                      {months.map(month => <option key={month} value={month}>{month}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={modalStyles.label}>Year *</label>
                    <select style={modalStyles.select} value={transactionYear} onChange={(e) => setTransactionYear(e.target.value)}>
                      {years.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                  </div>
                </div>
              )}
              
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Amount (UGX) *</label>
                <input 
                  type="text" 
                  style={modalStyles.input} 
                  value={transactionAmountDisplay} 
                  onChange={(e) => {
                    const raw = e.target.value;
                    let cleaned = raw.replace(/[^0-9.]/g, '');
                    const parts = cleaned.split('.');
                    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                    const numValue = parseFloat(cleaned);
                    const formatted = isNaN(numValue) ? '' : formatMoney(numValue);
                    setTransactionAmountDisplay(formatted);
                    setTransactionAmountRaw(isNaN(numValue) ? '' : numValue);
                  }} 
                  placeholder="0" 
                  required 
                />
                <small style={{ color: transactionType === 'penalty' ? '#ef4444' : '#10b981', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {transactionType === 'penalty' ? '⚠️ This amount will be SUBTRACTED from the balance' : '💰 This amount will be ADDED to the balance'}
                </small>
              </div>
              
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Description / Reason *</label>
                <textarea style={{...modalStyles.input, ...modalStyles.textarea}} value={transactionDescription} onChange={(e) => setTransactionDescription(e.target.value)} rows="3" required />
              </div>
            </div>
            <div style={modalStyles.footer}>
              <button style={modalStyles.buttonSecondary} onClick={() => setShowTransactionModal(false)}>Cancel</button>
              <button style={modalStyles.buttonPrimary} onClick={handleAddTransaction} disabled={transactionLoading || !transactionAmountRaw || parseFloat(transactionAmountRaw) <= 0 || (transactionType === 'salary' && !transactionMonth)}>
                {transactionLoading ? 'Processing...' : 'Update Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Detail Modal */}
      {showModal && selectedAccount && (
        <div style={modalStyles.overlay} onClick={() => setShowModal(false)}>
          <div style={{...modalStyles.container, maxWidth: '1100px'}} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>{selectedAccount.name}</h2>
              <div style={modalStyles.headerButtons}>
                <button style={modalStyles.printButton} onClick={() => generateStatement(selectedAccount)}>
                  🖨️ Print Statement
                </button>
                <button style={modalStyles.closeButton} onClick={() => setShowModal(false)}>×</button>
              </div>
            </div>
            
            <div style={modalStyles.content}>
              <div style={detailStyles.infoGrid}>
                <div style={detailStyles.infoCard}>
                  <div style={detailStyles.infoIcon}>📋</div>
                  <div>
                    <div style={detailStyles.infoLabel}>Account Type</div>
                    <div style={detailStyles.infoValue}>{selectedAccount.type === 'employee' ? 'Employee' : selectedAccount.type === 'subcontractor' ? 'Subcontractor' : 'Supplier'}</div>
                  </div>
                </div>
                <div style={detailStyles.infoCard}>
                  <div style={detailStyles.infoIcon}>📞</div>
                  <div>
                    <div style={detailStyles.infoLabel}>Phone</div>
                    <div style={detailStyles.infoValue}>{selectedAccount.phone || 'N/A'}</div>
                  </div>
                </div>
                <div style={detailStyles.infoCard}>
                  <div style={detailStyles.infoIcon}>📝</div>
                  <div>
                    <div style={detailStyles.infoLabel}>Description</div>
                    <div style={detailStyles.infoValue}>{selectedAccount.description || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div style={detailStyles.financialSection}>
                <h3 style={detailStyles.sectionTitle}>Financial Summary</h3>
                <div style={detailStyles.financialGrid}>
                  <div style={detailStyles.financialCard}>
                    <div style={detailStyles.financialLabel}>Amount Paid</div>
                    <div style={detailStyles.financialValue}>{(selectedAccount.paidAmount || 0).toLocaleString()} UGX</div>
                  </div>
                  <div style={{...detailStyles.financialCard, background: '#fef3c7'}}>
                    <div style={detailStyles.financialLabel}>Balance Due</div>
                    <div style={{...detailStyles.financialValue, color: '#d97706'}}>{(selectedAccount.currentBalance || 0).toLocaleString()} UGX</div>
                  </div>
                </div>
              </div>

              {selectedAccount.transactionHistory && selectedAccount.transactionHistory.length > 0 && (
                <div style={detailStyles.tableSection}>
                  <h3 style={detailStyles.sectionTitle}>📊 Transaction History</h3>
                  <div style={detailStyles.tableWrapper}>
                    <table style={detailStyles.sleekTable}>
                      <thead>
                        <tr>
                          <th style={detailStyles.sleekTableTh}>Date</th>
                          <th style={detailStyles.sleekTableTh}>Type</th>
                          <th style={detailStyles.sleekTableTh}>Amount</th>
                          <th style={detailStyles.sleekTableTh}>Description</th>
                          <th style={detailStyles.sleekTableTh}>Period</th>
                          <th style={detailStyles.sleekTableTh}>Balance After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTransactionHistory.map((transaction, idx) => (
                          <tr key={idx}>
                            <td style={detailStyles.sleekTableTd}>{transaction.date?.toDate ? new Date(transaction.date.toDate()).toLocaleDateString() : 'N/A'}</td>
                            <td style={detailStyles.sleekTableTd}>
                              <span style={detailStyles.getTypeBadge(transaction.type)}>
                                {transaction.type === 'salary' ? '💰 Salary' : transaction.type === 'bonus' ? '🎁 Bonus' : transaction.type === 'penalty' ? '⚠️ Penalty' : '📝 Adjustment'}
                              </span>
                            </td>
                            <td style={{...detailStyles.sleekTableTd, color: transaction.type === 'penalty' ? '#dc2626' : '#10b981', fontWeight: '600'}}>
                              {transaction.type === 'penalty' ? '-' : '+'}{transaction.amount?.toLocaleString()} UGX
                            </td>
                            <td style={{...detailStyles.sleekTableTd, maxWidth: '250px'}}>{transaction.description}</td>
                            <td style={detailStyles.sleekTableTd}>{transaction.period || '-'}</td>
                            <td style={{...detailStyles.sleekTableTd, fontWeight: '600'}}>{transaction.newBalance?.toLocaleString()} UGX</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {transactionTotalPages > 1 && (
                      <div style={detailStyles.tablePagination}>
                        <button onClick={() => setTransactionPage(p => Math.max(1, p - 1))} disabled={transactionPage === 1}>Previous</button>
                        <span>Page {transactionPage} of {transactionTotalPages}</span>
                        <button onClick={() => setTransactionPage(p => Math.min(transactionTotalPages, p + 1))} disabled={transactionPage === transactionTotalPages}>Next</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedAccount.type === 'employee' && selectedAccount.salaryHistory && selectedAccount.salaryHistory.length > 0 && (
                <div style={detailStyles.tableSection}>
                  <h3 style={detailStyles.sectionTitle}>💰 Salary History</h3>
                  <div style={detailStyles.tableWrapper}>
                    <table style={detailStyles.sleekTable}>
                      <thead>
                        <tr>
                          <th style={detailStyles.sleekTableTh}>Period</th>
                          <th style={detailStyles.sleekTableTh}>Amount</th>
                          <th style={detailStyles.sleekTableTh}>Status</th>
                          <th style={detailStyles.sleekTableTh}>Paid Amount</th>
                          <th style={detailStyles.sleekTableTh}>Paid Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSalaryHistory.map((entry, idx) => (
                          <tr key={idx}>
                            <td style={detailStyles.sleekTableTd}>{entry.period || `${entry.month} ${entry.year}`}</td>
                            <td style={detailStyles.sleekTableTd}>{(entry.amount || 0).toLocaleString()} UGX</td>
                            <td style={detailStyles.sleekTableTd}>
                              <span style={detailStyles.getStatusBadge(entry.status)}>
                                {entry.status === 'paid' ? '✓ Paid' : entry.status === 'partial' ? '⏳ Partial' : '⚠️ Pending'}
                              </span>
                            </td>
                            <td style={detailStyles.sleekTableTd}>{entry.paidAmount ? `${entry.paidAmount.toLocaleString()} UGX` : '-'}</td>
                            <td style={detailStyles.sleekTableTd}>{entry.paidDate?.toDate ? new Date(entry.paidDate.toDate()).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {salaryTotalPages > 1 && (
                      <div style={detailStyles.tablePagination}>
                        <button onClick={() => setSalaryPage(p => Math.max(1, p - 1))} disabled={salaryPage === 1}>Previous</button>
                        <span>Page {salaryPage} of {salaryTotalPages}</span>
                        <button onClick={() => setSalaryPage(p => Math.min(salaryTotalPages, p + 1))} disabled={salaryPage === salaryTotalPages}>Next</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedAccount.paymentHistory && selectedAccount.paymentHistory.length > 0 && (
                <div style={detailStyles.tableSection}>
                  <h3 style={detailStyles.sectionTitle}>💳 Payment History</h3>
                  <div style={detailStyles.tableWrapper}>
                    <table style={detailStyles.sleekTable}>
                      <thead>
                        <tr>
                          <th style={detailStyles.sleekTableTh}>Date</th>
                          <th style={detailStyles.sleekTableTh}>Amount</th>
                          <th style={detailStyles.sleekTableTh}>Method</th>
                          <th style={detailStyles.sleekTableTh}>Reference</th>
                          <th style={detailStyles.sleekTableTh}>Balance After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPaymentHistory.map((payment, idx) => (
                          <tr key={idx}>
                            <td style={detailStyles.sleekTableTd}>{payment.date?.toDate ? new Date(payment.date.toDate()).toLocaleDateString() : 'N/A'}</td>
                            <td style={{...detailStyles.sleekTableTd, color: '#10b981', fontWeight: '600'}}>{payment.amount.toLocaleString()} UGX</td>
                            <td style={detailStyles.sleekTableTd}>{payment.method || 'N/A'}</td>
                            <td style={detailStyles.sleekTableTd}>{payment.reference || '-'}</td>
                            <td style={{...detailStyles.sleekTableTd, fontWeight: '600'}}>{payment.newBalance?.toLocaleString()} UGX</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {paymentTotalPages > 1 && (
                      <div style={detailStyles.tablePagination}>
                        <button onClick={() => setPaymentPage(p => Math.max(1, p - 1))} disabled={paymentPage === 1}>Previous</button>
                        <span>Page {paymentPage} of {paymentTotalPages}</span>
                        <button onClick={() => setPaymentPage(p => Math.min(paymentTotalPages, p + 1))} disabled={paymentPage === paymentTotalPages}>Next</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(!selectedAccount.transactionHistory || selectedAccount.transactionHistory.length === 0) && 
               (!selectedAccount.salaryHistory || selectedAccount.salaryHistory.length === 0) && 
               (!selectedAccount.paymentHistory || selectedAccount.paymentHistory.length === 0) && (
                <div style={detailStyles.emptyState}>No transaction history available</div>
              )}
            </div>

            <div style={modalStyles.footer}>
              <button style={modalStyles.closeButtonFooter} onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Add Account Modal Component (with proper CSS) -----
const AddAccountModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    type: 'employee',
    name: '',
    phone: '',
    email: '',
    description: '',
    paymentType: 'monthly',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }
    setLoading(true);
    try {
      await onAdd(formData);
      onClose();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Use the same modal styles as other modals – they are already defined in modalStyles
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Add New Account</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Account Type *</label>
            <select
              style={modalStyles.select}
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData({ ...formData, type: newType, paymentType: newType === 'employee' ? 'monthly' : 'one_time' });
              }}
              required
            >
              <option value="employee">👨‍💼 Employee (Monthly Salary)</option>
              <option value="carpenter">👨‍💼 Carpenter</option>
              <option value="rent">👨‍💼Rent</option>
              <option value="loan">👨‍💼Loans</option>
              <option value="marketing">👨‍💼Marketing</option>
              <option value="subcontractor">👷 Subcontractor (One-time)</option>
              <option value="supplier">🏭 Supplier (One-time)</option>
            </select>
          </div>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Full Name *</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div style={modalStyles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Phone</label>
              <input
                type="tel"
                style={modalStyles.input}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Email</label>
              <input
                type="email"
                style={modalStyles.input}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Description / Notes</label>
            <textarea
              style={{...modalStyles.input, ...modalStyles.textarea}}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="2"
            />
          </div>
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" style={modalStyles.buttonPrimary} disabled={loading}>
              {loading ? 'Saving...' : 'Add Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ----- Styles (defined after components) -----
const styles = {
  container: { padding: '28px', background: '#f8fafc', minHeight: '100vh' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#64748b' },
  headerActions: { display: 'flex', gap: '12px', alignItems: 'center' },
  addButton: { background: '#7f1a2e', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  infoNote: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#eff6ff', borderRadius: '10px', marginBottom: '20px', fontSize: '12px', color: '#1e40af' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '28px' },
  summaryCard: { background: '#ffffff', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid #eef2f6' },
  summaryIcon: { fontSize: '32px' },
  summaryLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '4px' },
  summaryValue: { fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  warningCard: { borderLeft: '3px solid #f59e0b' },
  filtersBar: { display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
  filterLabel: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  filterInput: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', background: '#ffffff', width: '200px', outline: 'none' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6', background: '#ffffff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '14px 16px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '14px 16px', color: '#334155' },
  balanceDue: { color: '#f59e0b', fontWeight: '600' },
  balanceZero: { color: '#10b981', fontWeight: '600' },
  statusBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  actionButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  transactionButton: { padding: '6px 12px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  viewButton: { padding: '6px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px', padding: '16px' },
  pageButton: { padding: '8px 16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  accountInfoBox: { padding: '16px', background: '#f8fafc', borderRadius: '12px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  container: {
    background: '#ffffff',
    borderRadius: '24px',
    width: '90%',
    maxWidth: '550px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6',
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  printButton: {
    padding: '8px 16px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8',
  },
  content: { padding: '24px' },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #eef2f6',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  closeButtonFooter: {
    padding: '8px 20px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#475569',
  },
  buttonPrimary: {
    padding: '8px 20px',
    background: '#7f1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonSecondary: {
    padding: '8px 20px',
    background: '#f1f5f9',
    color: '#334155',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  form: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#334155', marginBottom: '8px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  textarea: { resize: 'vertical', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#ffffff', cursor: 'pointer' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px' },
};

const detailStyles = {
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  infoCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #eef2f6' },
  infoIcon: { fontSize: '24px' },
  infoLabel: { fontSize: '11px', fontWeight: '500', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoValue: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginTop: '4px' },
  financialSection: { marginBottom: '24px' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' },
  financialGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  financialCard: { textAlign: 'center', padding: '16px', background: '#f8fafc', borderRadius: '12px' },
  financialLabel: { fontSize: '11px', fontWeight: '500', color: '#64748b', marginBottom: '4px' },
  financialValue: { fontSize: '18px', fontWeight: '700', color: '#0f172a' },
  tableSection: { marginBottom: '24px' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6' },
  sleekTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  sleekTableTh: { textAlign: 'left', padding: '12px 16px', background: '#f8fafc', fontWeight: '600', color: '#475569', borderBottom: '1px solid #eef2f6' },
  sleekTableTd: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' },
  tablePagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', padding: '12px', borderTop: '1px solid #eef2f6' },
  getTypeBadge: (type) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    background: type === 'salary' ? '#dbeafe' : type === 'bonus' ? '#d1fae5' : type === 'penalty' ? '#fee2e2' : '#fef3c7',
    color: type === 'salary' ? '#2563eb' : type === 'bonus' ? '#059669' : type === 'penalty' ? '#dc2626' : '#d97706',
  }),
  getStatusBadge: (status) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
    background: status === 'paid' ? '#d1fae5' : status === 'partial' ? '#fef3c7' : '#fee2e2',
    color: status === 'paid' ? '#059669' : status === 'partial' ? '#d97706' : '#dc2626',
  }),
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default AllAccounts;